import { copyPlanEditData, copyPlanEditTargets, readPlanEditContext, samePlanEditContext } from './plan_edit_commands.mjs';

export const PLAN_INTERACTION_METHODS = Object.freeze(['tools', 'readSelection', 'clearSelection', 'selectHit', 'selectBox',
  'overlay', 'beginGesture', 'previewGesture', 'acknowledgeGesture', 'commitGesture', 'cancelGesture']);
const sync = value => {
  if (value && typeof value.then === 'function') { Promise.resolve(value).catch(() => undefined); throw new TypeError('Plan interaction must be synchronous'); }
  return value;
};
/** Owner-bound viewport port. No plugin runtime, model reference or callback crosses it. */
export function createOwnerPlanInteraction(owner, methods, { getContext, canInteract, synchronize, onDispose }) {
  let disposed = false, busy = false, gesture = null, acknowledged = null;
  const call = (name, ...args) => { const method = methods[name]; return sync(method(...args)); };
  const allowed = () => !disposed && !busy && sync(canInteract()) === true && readPlanEditContext(getContext);
  const read = () => disposed ? [] : copyPlanEditTargets(call('readSelection'), owner);
  const key = () => JSON.stringify(read());
  const current = () => allowed() && gesture && samePlanEditContext(gesture.context, readPlanEditContext(getContext)) && gesture.selection === key();
  function cancel() { gesture = acknowledged = null; if (!disposed) call('cancelGesture'); }
  const hit = value => {
    const next = copyPlanEditData(value);
    if (next !== null && next.owner !== owner) throw new TypeError('Foreign hit');
    return next;
  };
  return Object.freeze({
    tools: () => copyPlanEditData(call('tools')), readSelection: read,
    clearSelection() { if (!allowed() || gesture) return false; call('clearSelection'); return true; },
    selectHit(value, options = {}) { if (!allowed() || gesture) return false; return call('selectHit', hit(value), copyPlanEditData(options)) === true; },
    selectBox(values, options = {}) {
      if (!allowed() || gesture) return false;
      const next = copyPlanEditData(values);
      if (!Array.isArray(next) || next.some(item => item.owner !== owner)) throw new TypeError('Foreign box selection');
      return call('selectBox', next, copyPlanEditData(options)) === true;
    },
    overlay(value) { return disposed ? {selected:[],hover:[],snap:null,box:null} : copyPlanEditData(call('overlay', copyPlanEditData(value))); },
    beginGesture(value) {
      if (!allowed()) return null;
      cancel(); const input = copyPlanEditData(value); hit(input.hit);
      const before = readPlanEditContext(getContext), result = copyPlanEditData(call('beginGesture', input));
      if (!samePlanEditContext(before, readPlanEditContext(getContext))) { cancel(); return null; }
      if (!result) return null;
      if (typeof result.kind !== 'string') { cancel(); throw new TypeError('Invalid gesture kind'); }
      gesture = { context: before, selection: key() }; return result;
    },
    previewGesture(point, options) {
      if (!current()) { cancel(); throw new Error('Stale gesture'); }
      return copyPlanEditData(call('previewGesture', copyPlanEditData(point), copyPlanEditData(options)));
    },
    acknowledgeGesture(proposal) {
      if (!current()) throw new Error('Stale gesture');
      const safe = copyPlanEditData(proposal); call('acknowledgeGesture', safe); acknowledged = JSON.stringify(safe);
    },
    async commitGesture(proposal) {
      if (!current() || acknowledged !== JSON.stringify(copyPlanEditData(proposal))) return {status:'stale',committed:false};
      busy = true; let committed = null;
      try {
        const result = copyPlanEditData(call('commitGesture', copyPlanEditData(proposal)));
        if (!['accepted','noop','rejected','blocked','unsupported','invalid-target'].includes(result?.status)) throw new TypeError('Invalid commit result');
        if (result.selection !== undefined) copyPlanEditTargets(result.selection, owner);
        if (result.status !== 'accepted') return {...result,committed:false};
        committed = true; await synchronize();
        return {...result,committed:true,synchronized:!disposed};
      } catch { return {status:'failed',code:'PLAN_INTERACTION_COMMIT_FAILED',committed}; }
      finally { busy = false; cancel(); }
    },
    cancelGesture: cancel,
    snapshot: () => ({disposed,busy,active:Boolean(gesture)}),
    dispose() { if (disposed) return; try { cancel(); } catch { /* Cleanup must not retain a registered session. */ } finally { disposed = true; onDispose(); } }
  });
}
